/**
 * Locale-keyed copy for the OLB (Online Booking) flow.
 *
 * Resolution order (see resolveClientApiCopy): exact locale (e.g. `es-ES`) ->
 * language prefix (e.g. `es`) -> default `en-GB`. The English bundle mirrors the
 * strings that were previously hardcoded in the ClientApi components verbatim, so
 * English forms render unchanged. Spanish copy per GOLD-2419.
 *
 * Capture-controlled copy (prompt, the appointment what-to-expect body, pre-OLB
 * loader text, privacy-policy URL) is authored per form in Capture and NOT here.
 * The accordion label around that body is static and locale-keyed, so it lives here.
 */

const enGB = {
  store: {
    heading: 'Choose a store',
    nearYou: 'Showing stores near',
    listTab: 'List',
    mapTab: 'Map',
    storesLegend: 'Stores',
    select: 'Select',
    continue: 'Continue',
    loading: 'Finding stores near you...',
    mapUnavailable: 'The map could not be loaded — please use the list view.',
    lookupFailed: {
      heading: 'We could not load nearby stores right now',
      subheading: 'We will call you to arrange your appointment.',
    },
  },
  address: {
    heading: 'Confirm your address',
    subheading:
      "We'll use your address to assess your roof's solar potential using satellite imagery",
    selectorLabel: 'Select your address',
    postcodeLabel: 'Postcode',
    selectedLabel: 'Selected address',
    continue: 'Continue',
    loading: 'Finding your address...',
    lookupFailed: {
      heading: 'We could not load your address right now',
      subheading: 'We will call you to continue.',
    },
    noResults: {
      heading: "We couldn't find any addresses for your postcode",
      subheading: 'We will call you to arrange your appointment.',
    },
  },
  slot: {
    back: '‹ Back',
    heading: 'Pick a date & time',
    bookingAt: 'Booking at ',
    continue: 'Continue',
    noSlots: 'No appointments available — try another date or a nearby store.',
    viewAll: 'View all appointments',
    fetchFailed: 'We could not load times for this store.',
    accordionLabel: 'What happens at an appointment?',
    previousDays: 'Previous days',
    nextDays: 'Next days',
    previousTimes: 'Previous times',
    nextTimes: 'Next times',
  },
  solarSlots: {
    back: '‹ Back',
    title: 'Choose your appointment',
    subheading:
      'Please choose a time when you and any other household decision-makers are available for a solar assessment appointment',
    accordionLabel: 'What happens at an appointment?',
    accordionBody:
      "Solar Panels experts will visit your home for a free home assessment - usually taking up to 1 hour 30 minutes. They'll leave you with a full breakdown of your next steps to going solar.",
    continue: 'Continue',
    loadFailed: {
      heading: 'We could not find any appointment times right now',
      subheading: 'We will call you to arrange your appointment.',
    },
  },
  review: {
    back: '‹ Back',
    heading: 'Review your appointment',
    subheading: 'Check the details below before confirming.',
    yourDetails: 'Your details',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    yourAppointment: 'Your appointment',
    clinic: 'Clinic',
    address: 'Address',
    appointment: 'Appointment',
    privacyPrefix: 'By confirming, you agree to our ',
    privacyLinkText: 'Privacy Policy',
    privacySuffix: '.',
    // Solar runs a home visit, so it carries its own consent wording and a fixed
    // privacy-policy link rather than the form's generic one. {supplier} is filled
    // from the collection's supplierId. English only; no Spanish solar launch.
    solarPrivacyPrefix: 'Your privacy is important to us. ',
    solarPrivacyLinkText: 'Privacy Policy',
    solarPrivacyLink: 'https://www2.mvfglobal.com/pp/b649e',
    solarPrivacySuffix:
      '. By submitting this booking, you consent to MVF, trading as The Eco Experts, sharing your details with {supplier} to arrange and discuss your solar appointment. {supplier} may contact you by telephone (including automated calls), SMS, email, post or OTT messaging services such as WhatsApp for this purpose. You can withdraw your consent at any time.',
    confirm: 'Confirm Appointment',
    // Cycled under the Confirm button while the supplier's booking call is in
    // flight (it can run for several seconds), so the wait reads as progress.
    bookingInProgress: [
      'Booking your appointment…',
      'Confirming your time slot…',
      'Almost done…',
    ],
    bookingFailed: {
      heading: "We couldn't confirm your appointment just now",
      subheading: "We'll call you to arrange it.",
    },
  },
  confirmation: {
    heading: 'Your appointment is confirmed!',
    subheading: "We've saved your booking. See the details below.",
    clinic: 'Clinic',
    address: 'Address',
    appointment: 'Appointment',
    reference: 'Reference',
  },
  inactivity: {
    heading: 'Are you still there?',
    subheading: 'Your booking is nearly complete.',
    confirm: "I'm still here",
  },
  // Shown when Outreach ends the journey before a booking (lead status FAILED
  // or ABANDONED), keyed by the messageKey values Rhino sends with it; `default`
  // covers a missing or unknown key.
  // TODO (GOLD-2689): placeholder wording until product signs off the copy.
  leadFailed: {
    default: {
      heading: "We couldn't continue with your booking just now",
      subheading: "We'll call you to arrange your appointment.",
    },
    'auto-qualification-failed': {
      heading: "We couldn't book your appointment online",
      subheading: 'One of our team will call you to go through the next steps.',
    },
    'generic-error': {
      heading: 'Something went wrong with your booking',
      subheading: "We'll call you to arrange your appointment.",
    },
    'session-expired': {
      heading: 'Your booking session has expired',
      subheading: "We'll call you to arrange your appointment.",
    },
  },
  solar: {
    title: 'Your roof assessment',
    subheading: 'Here is what we found for your roof.',
    savingsLabel: 'Est. Annual Savings',
    back: '‹ Back',
    continue: 'Continue',
    loading: 'Assessing your roof...',
    potentialTitle: 'Estimated Solar Potential',
    tiles: {
      suitableFaces: 'Suitable Faces',
      maxPanels: 'Max Panels',
      usableRoofArea: 'Usable Roof Area',
      avgSunHours: 'Avg Sun Hrs/Yr',
      systemCapacity: 'System Capacity',
      yearlyOutput: 'kWh/Year Output',
    },
    // Placeholder wording. The component interpolates solarPotentialAge between
    // prefix and suffix.
    roofChangePromptPrefix:
      'Some satellite data used for this assessment is from ',
    roofChangePromptSuffix: ' — Has your roof changed since then?',
    roofChangeFollowUp: 'What type of roof change was it?',
    roofChangeYes: 'Yes',
    roofChangeNo: 'No',
    roofChangeSummaryLabel: 'Roof changes',
    roofChangeSummaryNone: 'No changes',
    edit: 'Edit',
    options: {
      HOUSE_EXTENSION: 'House extension',
      ROOF_REPAIR: 'Roof repairs',
      LOFT_CONVERSION: 'Loft conversion',
      OTHER: 'Other',
    },
    segment: 'Segment',
    loadFailed: {
      heading: 'We could not assess your roof right now',
      subheading: 'We will call you to arrange your appointment.',
    },
    mapUnavailable: 'The map could not be loaded.',
    mapEmpty: 'No roof faces to display for this property.',
    pinLink: 'Not your property? Click here to select on map',
    pinModal: {
      title: 'Select your property',
      close: 'Close',
      instructions:
        'Drag the marker or click on the map to select your property location.',
      currentAddressLabel: 'Current address:',
      markerLabel: 'Drag to move',
      coordinatesLabel: 'Selected coordinates:',
      cancel: 'Cancel',
      confirm: 'Confirm Location',
    },
    savingsTooltip:
      'Estimated savings based on 35% self-consumption and current energy prices. Assumes you use 35% of generated electricity yourself (saving £0.26/kWh) and export the rest (earning £0.12/kWh). Your actual savings will depend on when you use electricity and your energy tariff.',
  },
  questions: {
    back: '‹ Back',
    continue: 'Continue',
    edit: 'Edit',
    progress: 'Question {n} of {x}',
  },
  progress: {
    step: 'Step {n} of {x}',
  },
};

const esES = {
  store: {
    heading: 'Elija un centro',
    nearYou: 'Mostrando centros cerca de',
    listTab: 'Lista',
    mapTab: 'Mapa',
    storesLegend: 'Centros',
    select: 'Seleccionar',
    continue: 'Continuar',
    loading: 'Buscando centros cercanos...',
    mapUnavailable:
      'No se ha podido cargar el mapa. Utilice la vista de lista.',
    lookupFailed: {
      heading: 'No hemos podido cargar centros cercanos en este momento',
      subheading: 'Le llamaremos para concertar su cita.',
    },
  },
  slot: {
    back: '‹ Atrás',
    heading: 'Elija fecha y hora',
    bookingAt: 'Reservando en ',
    continue: 'Continuar',
    noSlots: 'No hay citas disponibles. Pruebe otra fecha o un centro cercano.',
    viewAll: 'Ver todas las citas',
    fetchFailed: 'No se han podido cargar las horas de este centro.',
    accordionLabel: '¿Qué ocurre en una cita?',
    previousDays: 'Días anteriores',
    nextDays: 'Días siguientes',
    previousTimes: 'Horas anteriores',
    nextTimes: 'Horas siguientes',
  },
  review: {
    back: '‹ Atrás',
    heading: 'Revise su cita',
    subheading: 'Compruebe los detalles antes de confirmar.',
    yourDetails: 'Sus datos',
    name: 'Nombre',
    email: 'Correo electrónico',
    phone: 'Teléfono',
    yourAppointment: 'Su cita',
    clinic: 'Centro',
    address: 'Dirección',
    appointment: 'Cita',
    privacyPrefix: 'Al confirmar, acepta nuestra ',
    privacyLinkText: 'Política de Privacidad',
    privacySuffix: '.',
    confirm: 'Confirmar cita',
    bookingInProgress: [
      'Reservando su cita…',
      'Confirmando su hora…',
      'Ya casi está…',
    ],
    bookingFailed: {
      heading: 'No hemos podido confirmar su cita en este momento',
      subheading: 'Le llamaremos para concertarla.',
    },
  },
  confirmation: {
    heading: '¡Su cita está confirmada!',
    subheading:
      'Hemos guardado su reserva. Consulte los detalles a continuación.',
    clinic: 'Centro',
    address: 'Dirección',
    appointment: 'Cita',
    reference: 'Referencia',
  },
  inactivity: {
    heading: '¿Sigue ahí?',
    subheading: 'Su cita está casi lista.',
    confirm: 'Sigo aquí',
  },
  // TODO (GOLD-2689): placeholder wording until product signs off the copy.
  leadFailed: {
    default: {
      heading: 'No hemos podido continuar con su reserva en este momento',
      subheading: 'Le llamaremos para concertar su cita.',
    },
    'auto-qualification-failed': {
      heading: 'No hemos podido reservar su cita en línea',
      subheading:
        'Uno de nuestros agentes le llamará para explicarle los próximos pasos.',
    },
    'generic-error': {
      heading: 'Se ha producido un error con su reserva',
      subheading: 'Le llamaremos para concertar su cita.',
    },
    'session-expired': {
      heading: 'Su sesión de reserva ha caducado',
      subheading: 'Le llamaremos para concertar su cita.',
    },
  },
  questions: {
    back: '‹ Atrás',
    continue: 'Continuar',
    edit: 'Editar',
    progress: 'Pregunta {n} de {x}',
  },
  progress: {
    step: 'Paso {n} de {x}',
  },
};

const BUNDLES = { 'en-GB': enGB, 'es-ES': esES };
const BY_LANGUAGE = { en: enGB, es: esES };
const DEFAULT_LOCALE = 'en-GB';
const DEFAULT_LANGUAGE = 'en';

/**
 * Resolves a form locale code (e.g. `es_ES`) to a language the OLB supports,
 * falling back to English. Shared with the slot date/time helpers so locale
 * codes are parsed in one place only.
 */
export const resolveLanguage = (localeCode) => {
  if (!localeCode) return DEFAULT_LANGUAGE;

  const language = localeCode.replace('_', '-').split('-')[0].toLowerCase();
  return BY_LANGUAGE[language] ? language : DEFAULT_LANGUAGE;
};

/**
 * Resolves the OLB copy bundle for a form locale code (e.g. `es_ES`), falling
 * back from exact locale to language prefix to English. A block missing from
 * the resolved bundle (e.g. a step only translated in English so far) falls back
 * to its English block, so a partly-translated bundle renders instead of
 * throwing.
 */
export const resolveClientApiCopy = (localeCode) => {
  const resolved = (() => {
    if (!localeCode) return BUNDLES[DEFAULT_LOCALE];
    const normalised = localeCode.replace('_', '-');
    if (BUNDLES[normalised]) return BUNDLES[normalised];
    return BY_LANGUAGE[resolveLanguage(normalised)];
  })();

  return { ...enGB, ...resolved };
};

/**
 * The lead-failed notice copy for the messageKey Outreach sent with the failed
 * status. A missing or unknown key (or one that is not an own entry, e.g.
 * `constructor`) resolves to the bundle's `default` entry, so an inherited
 * Object property is never rendered as copy and the notice is never empty.
 */
export const getLeadFailedCopy = (copy, messageKey) => {
  const { leadFailed } = copy;
  if (
    messageKey &&
    Object.prototype.hasOwnProperty.call(leadFailed, messageKey)
  ) {
    return leadFailed[messageKey];
  }
  return leadFailed.default;
};
